import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { useCurrentReseller } from '../../api/reseller';
import { useResellerCms, saveResellerCmsSection, type AdminCmsSection, type CmsSectionValue } from '../../api/cms';

/**
 * Reseller marketing-content editor — mirrors the platform-admin Cms.tsx editor
 * (§WS5b), scoped to the 3 sections a reseller may own: its own home page hero +
 * CTA (Landing.tsx) and its own pricing-page intro (Pricing.tsx). Unlike the admin
 * editor (whose 4 platform sections are always pre-seeded), a reseller may have no
 * row yet for a section — the field then shows empty with the live default as
 * placeholder text, and saving writes only what's filled in (an empty field falls
 * back to the platform/default copy on the live page, it isn't blanked out).
 */

interface FieldSpec {
  key: string;
  label: string;
  placeholder: string;
  rows?: number;
}
interface SectionSpec {
  key: string;
  label: string;
  fields: FieldSpec[];
}

export function Content() {
  const { data: currentReseller } = useCurrentReseller();
  const { data: sections = [], isLoading } = useResellerCms();
  const resellerName = currentReseller?.displayName || 'your business';

  const specs: SectionSpec[] = [
    {
      key: 'landing.hero',
      label: 'Homepage hero',
      fields: [
        { key: 'headline', label: 'Headline', placeholder: `Run your shop with ${resellerName} — built for Bangladesh.` },
        {
          key: 'subhead',
          label: 'Subhead',
          placeholder:
            'Cloud POS with HRM, CRM, manufacturing, repair, and WooCommerce sync. Pay in BDT, get cash or SSLCommerz. Built for shops, restaurants, pharmacies, and workshops.',
          rows: 2,
        },
      ],
    },
    {
      key: 'landing.cta',
      label: 'Homepage call-to-action button',
      fields: [{ key: 'text', label: 'Button text', placeholder: 'Start free trial' }],
    },
    {
      key: 'pricing.intro',
      label: 'Pricing page intro',
      fields: [
        { key: 'headline', label: 'Headline', placeholder: 'Simple pricing, in taka.' },
        {
          key: 'subhead',
          label: 'Subhead',
          placeholder: `Start free for 7 days, no card. Pick a plan when you’re ready. Pay online with SSLCommerz, or pay ${resellerName} directly.`,
          rows: 2,
        },
      ],
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Brand"
        title="Content"
        subtitle="Edit the copy on your own homepage and pricing page. Leave a field blank to keep the default copy."
      />
      {isLoading ? (
        <Card><div className="p-6 space-y-3" aria-busy="true"><div className="skeleton h-5 w-40 rounded" /><div className="skeleton h-24 rounded" /></div></Card>
      ) : (
        <div className="space-y-5">
          {specs.map((spec) => (
            <SectionEditor key={spec.key} spec={spec} existing={sections.find((s) => s.sectionKey === spec.key)} />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionEditor({ spec, existing }: { spec: SectionSpec; existing: AdminCmsSection | undefined }) {
  const qc = useQueryClient();
  const initial: CmsSectionValue = Object.fromEntries(spec.fields.map((f) => [f.key, existing?.value?.[f.key] ?? '']));
  const [draft, setDraft] = useState<CmsSectionValue>(initial);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  async function save() {
    setBusy(true);
    setSaved(false);
    try {
      await saveResellerCmsSection(spec.key, draft);
      await qc.invalidateQueries({ queryKey: ['reseller-cms'] });
      await qc.invalidateQueries({ queryKey: ['cms'] });
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-zinc-950 dark:text-zinc-50">{spec.label}</h2>
        <span className="text-xs text-zinc-400 dark:text-zinc-500 mono">{spec.key}</span>
      </div>
      <div className="mt-4 space-y-3">
        {spec.fields.map((field) => (
          <label key={field.key} className="block">
            <span className="block text-xs font-medium text-zinc-600 dark:text-zinc-300 mb-1">{field.label}</span>
            <textarea
              rows={field.rows ?? 1}
              value={draft[field.key] ?? ''}
              placeholder={field.placeholder}
              onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))}
              className="w-full px-3 py-2 rounded-md text-sm border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 focus:ring-2 focus:ring-brand/40 focus:border-brand resize-y placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
            />
          </label>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} disabled={busy || !dirty} iconLeft={<Save className="w-4 h-4" />}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {saved && !dirty && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
      </div>
    </Card>
  );
}
